import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  // Main assessment actions
  getFunctionalSafetyAssessment,
  upsertFunctionalSafetyAssessment,

  // Sub-assessment actions
  createMTTFAssessment,
  updateMTTFAssessment,
  deleteMTTFAssessment,
  createDCavgAssessment,
  updateDCavgAssessment,
  deleteDCavgAssessment,
  createCCFAssessment,
  deleteCCFAssessment,
  createHSRAssessment,
  deleteHSRAssessment,

  // Options actions
  getCCFOptions,
  getHSROptions,

  // Stats actions
  getFunctionalSafetyStats,

  // UI actions
  setActiveModal,
  clearActiveModal,
  clearError,
  clearAll,
} from '../redux/functionalSafety/functionalSafetySlice';

import {
  // Main assessment selectors
  getFunctionalSafetyAssessment as getAssessmentSelector,
  getFunctionalSafetyAssessments,
  
  // Sub-assessments selectors
  getMTTFAssessments,
  getDCavgAssessments,
  getCCFAssessments,
  getHSRAssessments,
  
  // Static data selectors
  getCCFOptions as getCCFOptionsSelector,
  getHSROptions as getHSROptionsSelector,
  
  // Loading selectors
  getFunctionalSafetyLoading,
  getFunctionalSafetyCreating,
  getFunctionalSafetyUpdating,
  
  // Error selector
  getFunctionalSafetyError,
  
  // UI selectors
  getActiveModal,
  getAssessmentCompletionStatus,
  getAssessmentScores,
} from '../redux/selectors/functionalSafety';
import { FUNCTIONAL_SAFETY_CHANNEL_TYPES } from '../utils/constants';

/**
 * Custom hook for Functional Safety Assessment
 * Handles all logic, API calls, and UI state management
 *
 * @param {number} taskId - The task ID
 * @returns {object} - Complete hook interface
 */
const useFunctionalSafety = (taskId) => {
  const dispatch = useDispatch();
  
  // Redux state
  const assessment = useSelector(getAssessmentSelector);
  const assessments = useSelector(getFunctionalSafetyAssessments);
  const mttfAssessments = useSelector(getMTTFAssessments);
  const dcavgAssessments = useSelector(getDCavgAssessments);
  const ccfAssessments = useSelector(getCCFAssessments);
  const hsrAssessments = useSelector(getHSRAssessments);
  const ccfOptions = useSelector(getCCFOptionsSelector);
  const hsrOptions = useSelector(getHSROptionsSelector);
  
  // Loading states
  const isLoading = useSelector(getFunctionalSafetyLoading);
  const isCreating = useSelector(getFunctionalSafetyCreating);
  const isUpdating = useSelector(getFunctionalSafetyUpdating);
  
  // Error state
  const error = useSelector(getFunctionalSafetyError);
  
  // UI state
  const activeModal = useSelector(getActiveModal);
  const completionStatus = useSelector(getAssessmentCompletionStatus);
  const scores = useSelector(getAssessmentScores);
  
  // Local state for form data
  const [formData, setFormData] = useState({
    hydraulic_system: false,
    category: '',
    hsr_method: false,
    show_dcavg: false,
    show_ccf: false,
  });
  
  const [modalData, setModalData] = useState({});
  
  useEffect(() => {
    
    dispatch(getCCFOptions());
    dispatch(getHSROptions());
    
  }, []); 

  
  useEffect(() => {
    if (taskId) {
     

      
      dispatch(clearAll());

      
      dispatch(getFunctionalSafetyAssessment({ task: taskId }));
      
      // Fetch stats separately
      dispatch(getFunctionalSafetyStats({ task: taskId }));
    } 

  
    return () => {
     
      dispatch(clearAll());
    };
   
  }, [taskId, dispatch]);
  
 
  useEffect(() => {
    

    if (assessment) {
      const newFormData = {
        hydraulic_system: assessment.hydraulic_system || false,
        category: assessment.category || '',
        hsr_method: assessment.hsr_method || false,
        show_dcavg: assessment.show_dcavg || false,
        show_ccf: assessment.show_ccf || false,
      };
      
      setFormData(newFormData);
    }
  }, [assessment]);
  
  // API Functions
  const fetchAssessment = useCallback(async () => {
    if (taskId) {
    
      dispatch(getFunctionalSafetyAssessment({
        task: taskId
      }));
    } 
  }, [dispatch, taskId]);
  
  const fetchOptions = useCallback(async () => {
    dispatch(getCCFOptions());
    dispatch(getHSROptions());
  }, [dispatch]);
  
  const upsertAssessment = useCallback(async (data) => {
   
    const payload = {
      task: taskId,
      ...data
    };
   
    dispatch(upsertFunctionalSafetyAssessment(payload));
  }, [dispatch, taskId]);
  
  // Sub-assessment functions
  const createMTTF = useCallback(async (assessmentData) => {
    if (assessment?.id) {
      dispatch(createMTTFAssessment({
        assessmentId: assessment.id,
        assessmentData: assessmentData,
        taskId: taskId
      }));
    }
  }, [dispatch, assessment?.id, taskId]);

  const updateMTTF = useCallback(async (mttfId, assessmentData) => {
    if (assessment?.id) {
      dispatch(updateMTTFAssessment({
        assessmentId: assessment.id,
        mttfId: mttfId,
        assessmentData: assessmentData,
        taskId: taskId
      }));
    }
  }, [dispatch, assessment?.id, taskId]);

  const deleteMTTF = useCallback(async (mttfId) => {
    if (assessment?.id) {
      dispatch(deleteMTTFAssessment({
        assessmentId: assessment.id,
        mttfId: mttfId,
        taskId: taskId
      }));
    }
  }, [dispatch, assessment?.id, taskId]);
  
  const createDCavg = useCallback(async (assessmentData) => {
    if (assessment?.id) {
      dispatch(createDCavgAssessment({
        assessmentId: assessment.id,
        assessmentData: assessmentData,
        taskId: taskId
      }));
    }
  }, [dispatch, assessment?.id, taskId]);

  const updateDCavg = useCallback(async (dcavgId, assessmentData) => {
    if (assessment?.id) {
      dispatch(updateDCavgAssessment({
        assessmentId: assessment.id,
        dcavgId: dcavgId,
        assessmentData: assessmentData,
        taskId: taskId
      }));
    }
  }, [dispatch, assessment?.id, taskId]);

  const deleteDCavg = useCallback(async (dcavgId) => {
    if (assessment?.id) {
      dispatch(deleteDCavgAssessment({
        assessmentId: assessment.id,
        dcavgId: dcavgId,
        taskId: taskId
      }));
    }
  }, [dispatch, assessment?.id, taskId]);
  
  const createCCF = useCallback(async (assessmentData) => {
    if (assessment?.id) {
      dispatch(createCCFAssessment({
        assessmentId: assessment.id,
        assessmentData: assessmentData,
        taskId: taskId
      }));
    }
  }, [dispatch, assessment?.id, taskId]);

  const deleteCCF = useCallback(async (ccfId) => {
    if (assessment?.id) {
      dispatch(deleteCCFAssessment({
        assessmentId: assessment.id,
        ccfId: ccfId,
        taskId: taskId
      }));
    }
  }, [dispatch, assessment?.id, taskId]);

  const createHSR = useCallback(async (assessmentData) => {
    if (assessment?.id) {
      dispatch(createHSRAssessment({
        assessmentId: assessment.id,
        assessmentData: assessmentData,
        taskId: taskId
      }));
    }
  }, [dispatch, assessment?.id, taskId]);

  const deleteHSR = useCallback(async (hsrId) => {
    if (assessment?.id) {
      dispatch(deleteHSRAssessment({
        assessmentId: assessment.id,
        hsrId: hsrId,
        taskId: taskId
      }));
    }
  }, [dispatch, assessment?.id, taskId]);
  
  // Modal functions
  const openModal = useCallback((modalName, data = {}) => {
    setModalData(data);
    dispatch(setActiveModal(modalName));
  }, [dispatch]);
  
  const closeModal = useCallback(() => {
    setModalData({});
    dispatch(clearActiveModal());
  }, [dispatch]);
  
  // Form handlers
  const handleFormChange = useCallback((field, value) => {
    

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

   
    upsertAssessment({ [field]: value });
  }, [upsertAssessment]);
  
  const handleModalSave = useCallback(async (modalName, data) => {
    try {
      switch (modalName) {
        case 'mttf':
          await createMTTF(data);
          break;
        case 'dcavg':
          await createDCavg(data);
          break;
        case 'ccf':
          await createCCF(data);
          break;
        case 'hsr':
          await createHSR(data);
          break;
        default:
          console.error('Unknown modal type:', modalName);
      }
      closeModal();
    } catch (error) {
      console.error('Failed to save modal data:', error);
    }
  }, [createMTTF, createDCavg, createCCF, createHSR, closeModal]);
  
  // Computed values
  const isModalOpen = useCallback((modalName) => activeModal === modalName, [activeModal]);
  
  const assessmentData = useMemo(() => {
    if (!assessment) return null;
    
    return {
      ...assessment,
      mttf_assessments: mttfAssessments,
      dcavg_assessments: dcavgAssessments,
      ccf_assessments: ccfAssessments,
      hsr_assessments: hsrAssessments,
    };
  }, [assessment, mttfAssessments, dcavgAssessments, ccfAssessments, hsrAssessments]);
  
  const groupedMTTFData = useMemo(() => {
    if (!mttfAssessments.length) return { input: [], logic: [], output: [], te: [], ote: [], input2: [], logic2: [], output2: [] };
    
    return {
      input: mttfAssessments.filter(item => item.component_type === FUNCTIONAL_SAFETY_CHANNEL_TYPES.INPUT),
      logic: mttfAssessments.filter(item => item.component_type === FUNCTIONAL_SAFETY_CHANNEL_TYPES.LOGIC),
      output: mttfAssessments.filter(item => item.component_type === FUNCTIONAL_SAFETY_CHANNEL_TYPES.OUTPUT),
      te: mttfAssessments.filter(item => item.component_type === FUNCTIONAL_SAFETY_CHANNEL_TYPES.TE),
      ote: mttfAssessments.filter(item => item.component_type === FUNCTIONAL_SAFETY_CHANNEL_TYPES.OTE),
      input2: mttfAssessments.filter(item => item.component_type === FUNCTIONAL_SAFETY_CHANNEL_TYPES.INPUT2),
      logic2: mttfAssessments.filter(item => item.component_type === FUNCTIONAL_SAFETY_CHANNEL_TYPES.LOGIC2),
      output2: mttfAssessments.filter(item => item.component_type === FUNCTIONAL_SAFETY_CHANNEL_TYPES.OUTPUT2),
    };
  }, [mttfAssessments]);
  
  const selectedCCFOptions = useMemo(() => {
    return ccfAssessments.map(assessment => assessment.ccf_option);
  }, [ccfAssessments]);
  
  const selectedHSROptions = useMemo(() => {
    return hsrAssessments.map(assessment => assessment.hsr_option);
  }, [hsrAssessments]);
  
  // Error handling
  const clearErrors = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);
  
  // Return complete hook interface
  return {
    // Data
    assessment: assessmentData,
    assessments,
    formData,
    modalData,
    
    // Sub-assessments
    mttfAssessments,
    dcavgAssessments,
    ccfAssessments,
    hsrAssessments,
    groupedMTTFData,
    
    // Static data
    ccfOptions,
    hsrOptions,
    selectedCCFOptions,
    selectedHSROptions,
    
    // Loading states
    isLoading,
    isCreating,
    isUpdating,
    
    // Error handling
    error,
    clearErrors,
    
    // UI state
    activeModal,
    completionStatus,
    scores,
    isModalOpen,
    
    // Actions
    fetchAssessment,
    upsertAssessment,
    handleFormChange,
    
    // Modal actions
    openModal,
    closeModal,
    handleModalSave,
    
    // Sub-assessment actions
    createMTTF,
    updateMTTF,
    deleteMTTF,
    createDCavg,
    updateDCavg,
    deleteDCavg,
    createCCF,
    deleteCCF,
    createHSR,
    deleteHSR,
    
    // Utility
    hasAssessment: !!assessment,
    isComplete: completionStatus ? Object.values(completionStatus).every(Boolean) : false,
  };
};

export default useFunctionalSafety;
